import React, { useState, useEffect } from 'react';
import { tareasAPI } from '../api/api';

const Tareas = () => {
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedTarea, setSelectedTarea] = useState(null);

  const estadosMap = {
    'pendiente': { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800' },
    'en_proceso': { label: 'En Proceso', color: 'bg-blue-100 text-blue-800' },
    'completada': { label: 'Completada', color: 'bg-green-100 text-green-800' },
    'cancelada': { label: 'Cancelada', color: 'bg-gray-100 text-gray-800' }
  };

  const prioridadesMap = {
    'baja': { label: 'Baja', color: 'bg-gray-100 text-gray-700' },
    'media': { label: 'Media', color: 'bg-blue-100 text-blue-700' },
    'alta': { label: 'Alta', color: 'bg-orange-100 text-orange-700' },
    'urgente': { label: 'Urgente', color: 'bg-red-100 text-red-700' }
  };

  const tiposMap = {
    'compra': 'Compra',
    'preparacion': 'Preparación',
    'revision_material': 'Revisión Material',
    'montaje': 'Montaje',
    'transporte': 'Transporte',
    'limpieza': 'Limpieza',
    'elaboracion': 'Elaboración',
    'otro': 'Otro'
  };

  useEffect(() => {
    fetchTareas();
  }, [filtroEstado, filtroTipo]);

  const fetchTareas = async () => {
    try {
      setLoading(true);
      const response = await tareasAPI.getAll(null, filtroEstado, filtroTipo);
      setTareas(response.data);
    } catch (error) {
      console.error('Error fetching tareas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompletarTarea = async (tareaId) => {
    try {
      await tareasAPI.completar(tareaId);
      fetchTareas();
    } catch (error) {
      console.error('Error completando tarea:', error);
      alert('Error al completar la tarea');
    }
  };

  const handleDeleteTarea = async (tareaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta tarea?')) return;
    
    try {
      await tareasAPI.delete(tareaId);
      fetchTareas();
    } catch (error) {
      console.error('Error deleting tarea:', error);
      alert('Error al eliminar la tarea');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const TareaCard = ({ tarea }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-4 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-800">{tarea.titulo}</h3>
              {tarea.auto_generada && (
                <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                  Auto
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${estadosMap[tarea.estado]?.color || 'bg-gray-100'}`}>
                {estadosMap[tarea.estado]?.label || tarea.estado}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${prioridadesMap[tarea.prioridad]?.color || 'bg-gray-100'}`}>
                {prioridadesMap[tarea.prioridad]?.label || tarea.prioridad}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                {tiposMap[tarea.tipo] || tarea.tipo}
              </span>
            </div>

            {tarea.descripcion && (
              <p className="text-gray-600 text-sm mb-3">{tarea.descripcion}</p>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              {tarea.fecha_limite && (
                <div>
                  <span className="font-medium">Fecha límite:</span> {formatFecha(tarea.fecha_limite)}
                </div>
              )}
              {tarea.tiempo_estimado_minutos && (
                <div>
                  <span className="font-medium">Tiempo estimado:</span>{' '}
                  {(() => {
                    const numPersonas = tarea.empleados_asignados?.length || 1;
                    const tiempoProporcional = tarea.tiempo_estimado_minutos / numPersonas;
                    return `${tiempoProporcional.toFixed(1)} min ${numPersonas > 1 ? `(${numPersonas} personas)` : ''}`;
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 ml-4">
            {tarea.estado === 'pendiente' && (
              <button
                onClick={() => handleCompletarTarea(tarea.id)}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition"
              >
                ✓ Completar
              </button>
            )}
            {!tarea.auto_generada && (
              <button
                onClick={() => handleDeleteTarea(tarea.id)}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition"
              >
                Eliminar
              </button>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition"
            >
              {expanded ? '▲ Menos' : '▼ Más'}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {tarea.checklist && tarea.checklist.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-700 mb-2">Checklist:</h4>
                <ul className="space-y-1">
                  {tarea.checklist.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <span className="mr-2">{item.completado ? '✓' : '○'}</span>
                      {item.item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {tarea.notas && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-700 mb-2">Notas:</h4>
                <p className="text-sm text-gray-600">{tarea.notas}</p>
              </div>
            )}

            {tarea.fecha_completada && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Completada:</span> {formatFecha(tarea.fecha_completada)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Cargando tareas...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">📋 Gestión de Tareas</h1>
        <button
          onClick={() => setShowModal(true)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          + Nueva Tarea Manual
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_proceso">En Proceso</option>
              <option value="completada">Completada</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="compra">Compra</option>
              <option value="preparacion">Preparación</option>
              <option value="revision_material">Revisión Material</option>
              <option value="elaboracion">Elaboración</option>
              <option value="montaje">Montaje</option>
              <option value="transporte">Transporte</option>
              <option value="limpieza">Limpieza</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFiltroEstado('');
                setFiltroTipo('');
              }}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-800">
            {tareas.filter(t => t.estado === 'pendiente').length}
          </div>
          <div className="text-sm text-yellow-700">Pendientes</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-2xl font-bold text-blue-800">
            {tareas.filter(t => t.estado === 'en_proceso').length}
          </div>
          <div className="text-sm text-blue-700">En Proceso</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-800">
            {tareas.filter(t => t.estado === 'completada').length}
          </div>
          <div className="text-sm text-green-700">Completadas</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="text-2xl font-bold text-purple-800">
            {tareas.filter(t => t.auto_generada).length}
          </div>
          <div className="text-sm text-purple-700">Automáticas</div>
        </div>
      </div>

      {/* Lista de tareas */}
      {tareas.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-gray-400 text-xl mb-2">No hay tareas</div>
          <p className="text-gray-500">Las tareas se generan automáticamente al confirmar presupuestos</p>
        </div>
      ) : (
        <div>
          {tareas.map(tarea => (
            <TareaCard key={tarea.id} tarea={tarea} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Tareas;
